import { Converter } from 'proj4';
import { Vector3 } from 'three';

import { TileCoord, WorldQuad } from './ortho-tile-typings';

// WebMercator world extent in meters
const R = 6378137;
const MAX = Math.PI * R; // ~20037508.342789244

function tileBounds3857(coord: TileCoord) {
  const n = 2 ** coord.z;

  const minX = -MAX + (2 * MAX * coord.x) / n;
  const maxX = -MAX + (2 * MAX * (coord.x + 1)) / n;

  // Note Y is flipped in XYZ
  const minY = MAX - (2 * MAX * (coord.y + 1)) / n;
  const maxY = MAX - (2 * MAX * coord.y) / n;

  return { minX, minY, maxX, maxY };
}

/**
 * Build a quad in scene coordinates for a given tile.
 * - tile coords are XYZ in EPSG:3857
 * - projectProjection is whatever your project uses
 * - You decide how project coords map to scene (scale + origin)
 */
export function makeTileBoundsToWorldQuad(params: {
  webMercatorToProject: Converter;
  sceneOriginProject: { x: number; y: number }; // project coords at scene (0,0)
}) {
  // Transform function: 3857 meters -> project coords
  const toProject = (x: number, y: number) => {
    const [px, py] = params.webMercatorToProject.forward([x, y]);
    return { x: px, y: py };
  };

  return (coord: TileCoord): WorldQuad => {
    const b = tileBounds3857(coord);

    // Corners in 3857 meters
    const sw3857 = { x: b.minX, y: b.minY };
    const se3857 = { x: b.maxX, y: b.minY };
    const ne3857 = { x: b.maxX, y: b.maxY };
    const nw3857 = { x: b.minX, y: b.maxY };

    // Convert to project coords
    const sw = toProject(sw3857.x, sw3857.y);
    const se = toProject(se3857.x, se3857.y);
    const ne = toProject(ne3857.x, ne3857.y);
    const nw = toProject(nw3857.x, nw3857.y);

    // Convert project coords to scene coords (x,y on ground plane; z up)
    const ox = params.sceneOriginProject.x;
    const oy = params.sceneOriginProject.y;

    return {
      // a,b,c,d order should match your UV convention in buildQuadGeometry
      a: new Vector3(sw.x - ox, sw.y - oy, 0),
      b: new Vector3(se.x - ox, se.y - oy, 0),
      c: new Vector3(ne.x - ox, ne.y - oy, 0),
      d: new Vector3(nw.x - ox, nw.y - oy, 0),
    };
  };
}
